import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/database.types';

type Task = Database['public']['Tables']['tasks']['Row'];
type TaskInsert = Database['public']['Tables']['tasks']['Insert'];
type TaskUpdate = Database['public']['Tables']['tasks']['Update'];


/**
 * API service for Task CRUD operations
 */
export const tasksApi = {
    /**
     * Get all tasks for a project
     */
    async getByProject(
        projectId: number
    ): Promise<{ data: Task[] | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('tasks')
                .select('*')
                .eq('project_id', projectId)
                .order('created_at', { ascending: true });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Get a single task by ID
     */
    async getById(id: number): Promise<{ data: Task | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('tasks')
                .select('*')
                .eq('id', id)
                .single();

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Create a new task
     */
    async create(task: TaskInsert): Promise<{ data: Task | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('tasks')
                .insert(task)
                .select()
                .single();

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Update an existing task
     */
    async update(
        id: number,
        updates: TaskUpdate
    ): Promise<{ data: Task | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('tasks')
                .update(updates)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Delete a task
     */
    async delete(id: number): Promise<{ error: Error | null }> {
        try {
            const { error } = await supabase.from('tasks').delete().eq('id', id);

            if (error) throw error;
            return { error: null };
        } catch (error) {
            return {
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Get tasks that depend on the given task (i.e., tasks that list taskId as a prerequisite)
     */
    async getTaskDependents(
        taskId: number
    ): Promise<{ data: { task_id: number; task: { id: number; name: string } | null }[] | null; error: Error | null }> {
        if (!Number.isInteger(taskId) || taskId <= 0) {
            return { data: [], error: null };
        }
        try {
            const { data, error } = await supabase
                .from('task_dependencies')
                .select('task_id, task:tasks!task_dependencies_task_id_fkey(id, name)')
                .eq('depends_on_task_id', taskId);

            if (error) throw error;
            return {
                data: data as { task_id: number; task: { id: number; name: string } | null }[],
                error: null,
            };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Atomically update dates for multiple tasks via the batch_update_task_dates RPC.
     * All updates are applied in a single transaction; on error none are persisted.
     */
    async batchUpdateDates(
        updates: Array<{ id: number; start_date: string; end_date: string }>
    ): Promise<{ error: Error | null }> {
        if (updates.length === 0) return { error: null };
        try {
            const { error } = await supabase.rpc('batch_update_task_dates', {
                updates: JSON.stringify(updates),
            });
            if (error) throw error;
            return { error: null };
        } catch (error) {
            return {
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Fetch all task_dependency rows for a project's tasks.
     * Returns rows with task_id and depends_on_task_id so callers can build
     * a dependency graph for shift calculations.
     */
    async getDependenciesByProject(
        projectId: number
    ): Promise<{ data: Array<{ task_id: number; depends_on_task_id: number }> | null; error: Error | null }> {
        try {
            // Two-step query: first fetch this project's task IDs, then filter
            // task_dependencies. A single-query PostgREST left-join filter
            // (.eq('tasks.project_id', ...)) returns rows from other projects
            // when the join produces null, polluting the dependency graph.
            const { data: projectTasks, error: taskErr } = await supabase
                .from('tasks')
                .select('id')
                .eq('project_id', projectId);
            if (taskErr) throw taskErr;

            const taskIds = (projectTasks ?? []).map((t) => t.id);
            if (taskIds.length === 0) return { data: [], error: null };

            const { data, error } = await supabase
                .from('task_dependencies')
                .select('task_id, depends_on_task_id')
                .in('task_id', taskIds);
            if (error) throw error;

            return { data: data ?? [], error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Get all tasks across multiple projects (for dashboard-level export).
     * Returns an empty array when projectIds is empty.
     */
    async getByProjectIds(
        projectIds: number[]
    ): Promise<{ data: Task[] | null; error: Error | null }> {
        if (projectIds.length === 0) return { data: [], error: null };
        try {
            const { data, error } = await supabase
                .from('tasks')
                .select('*')
                .in('project_id', projectIds)
                .order('project_id', { ascending: true })
                .order('display_order', { ascending: true });
            if (error) throw error;
            return { data: data ?? [], error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Get all task dependencies for tasks belonging to the given projects.
     */
    async getDependenciesByProjectIds(
        projectIds: number[]
    ): Promise<{ data: Array<{ task_id: number; depends_on_task_id: number }> | null; error: Error | null }> {
        if (projectIds.length === 0) return { data: [], error: null };
        try {
            const { data: projectTasks, error: taskErr } = await supabase
                .from('tasks')
                .select('id')
                .in('project_id', projectIds);
            if (taskErr) throw taskErr;

            const taskIds = (projectTasks ?? []).map(t => t.id);
            if (taskIds.length === 0) return { data: [], error: null };

            const { data, error } = await supabase
                .from('task_dependencies')
                .select('task_id, depends_on_task_id')
                .in('task_id', taskIds);
            if (error) throw error;

            return { data: data ?? [], error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Get tasks assigned to a team member
     */
    async getByAssignee(
        assignedMemberId: number
    ): Promise<{ data: Task[] | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('tasks')
                .select('*')
                .eq('assigned_member_id', assignedMemberId)
                .order('start_date', { ascending: true });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },
};

